import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { exportLeads } from '../api/use-leads';

interface ExportDialogProps {
  idPassbook: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Antifraude — exporta só leads bloqueados. */
  blocked?: boolean;
}

export function ExportDialog({ idPassbook, open, onOpenChange, blocked = false }: ExportDialogProps) {
  const [email, setEmail] = useState('');
  const [type, setType] = useState<'users' | 'logs'>('users');
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (!email.includes('@')) {
      toast.error('Informe um e-mail válido.');
      return;
    }
    setLoading(true);
    try {
      await exportLeads(idPassbook, email, type, { blocked });
      toast.success('Exportação iniciada. Você receberá o arquivo por e-mail.');
      onOpenChange(false);
    } catch {
      toast.error('Falha ao exportar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{blocked ? 'Exportar leads bloqueados' : 'Exportar leads'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de exportação</Label>
            <RadioGroup
              value={type}
              onValueChange={(v) => setType(v as 'users' | 'logs')}
              className="gap-2"
            >
              <label
                htmlFor="export-users"
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-[hsl(var(--glass-bg)/0.35)] p-3 transition-colors has-[[data-state=checked]]:border-primary/40 has-[[data-state=checked]]:bg-primary/[0.06]"
              >
                <RadioGroupItem value="users" id="export-users" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium">Base de leads</span>
                  <span className="block text-xs text-muted-foreground">
                    {blocked ? 'Cadastros bloqueados (antifraude)' : 'Cadastros e dados dos clientes'}
                  </span>
                </span>
              </label>
              <label
                htmlFor="export-logs"
                className="flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-[hsl(var(--glass-bg)/0.35)] p-3 transition-colors has-[[data-state=checked]]:border-primary/40 has-[[data-state=checked]]:bg-primary/[0.06]"
              >
                <RadioGroupItem value="logs" id="export-logs" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium">Logs</span>
                  <span className="block text-xs text-muted-foreground">Histórico de transações e eventos</span>
                </span>
              </label>
            </RadioGroup>
          </div>
          <div className="space-y-2">
            <Label htmlFor="export-email">E-mail</Label>
            <Input
              id="export-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@empresa.com.br"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleExport} loading={loading}>
            Exportar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
